import React, { Suspense, Fragment, lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import DashboardLayout from 'src/layouts/DashboardLayout';
import MainLayout from 'src/layouts/MainLayout';
import LoadingScreen from 'src/components/LoadingScreen';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={props => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <Component {...props} />
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/forgot-password',
    component: lazy(() => import('src/views/auth/ForgotPasswordView'))
  },
  {
    exact: true,
    path: '/login-unprotected',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/reset-request',
    component: lazy(() => import('src/views/auth/ResetRequestView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/reset-password',
    component: lazy(() => import('src/views/auth/ResetView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register',
    component: lazy(() => import('src/views/auth/RegisterView'))
  },
  {
    exact: true,
    path: '/register-unprotected',
    component: lazy(() => import('src/views/auth/RegisterView'))
  },
  {
    path: '/dashboard',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/dashboard',
        component: lazy(() =>
          import('src/views/reports/DashboardAlternativeView')
        )
      }
    ]
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app/support-tickets',
        component: lazy(() => import('src/components/SupportTickets/SupportTicketListPage'))
      },
      {
        exact: true,
        path: '/app/support-tickets/:ticketId',
        component: lazy(() => import('src/components/SupportTickets/SupportTicketPage'))
      },
      {
        exact: true,
        path: '/app/knowledge',
        component: lazy(() => import('src/components/ChatConfig/KnowledgeBasePage'))
      },
      {
        exact: true,
        path: '/app/account',
        component: () => <Redirect to="/app/reports" />
      },
      {
        exact: true,
        path: '/app/calendar',
        component: lazy(() => import('src/views/calendar/CalendarView'))
      },
      {
        exact: true,
        path: ['/app/chat/new', '/app/chat/:threadKey'],
        component: lazy(() => import('src/views/chat/ChatView'))
      },
      {
        exact: true,
        path: '/app/chat',
        component: () => <Redirect to="/app/chat/new" />
      },
      {
        exact: true,
        path: '/app/extra/charts/apex',
        component: lazy(() => import('src/views/extra/charts/ApexChartsView'))
      },
      {
        exact: true,
        path: '/app/extra/forms/formik',
        component: lazy(() => import('src/views/extra/forms/FormikView'))
      },
      {
        exact: true,
        path: '/app/extra/forms/redux',
        component: lazy(() => import('src/views/extra/forms/ReduxFormView'))
      },
      {
        exact: true,
        path: '/app/extra/editors/draft-js',
        component: lazy(() => import('src/views/extra/editors/DraftEditorView'))
      },
      {
        exact: true,
        path: '/app/extra/editors/quill',
        component: lazy(() => import('src/views/extra/editors/QuillEditorView'))
      },
      {
        exact: true,
        path: '/app/kanban',
        component: lazy(() => import('src/views/kanban/KanbanView'))
      },
      {
        exact: true,
        path: [
          '/app/mail/label/:customLabel/:mailId?',
          '/app/mail/:systemLabel/:mailId?'
        ],
        component: lazy(() => import('src/views/mail/MailView'))
      },
      {
        exact: true,
        path: '/app/mail',
        component: () => <Redirect to="/app/mail/all" />
      },
      {
        exact: true,
        path: '/app/management/types',
        component: lazy(() => import('src/views/types'))
      },
      {
        exact: true,
        path: '/app/management/teams',
        component: lazy(() => import('src/views/teams/TeamListView'))
      },
      {
        exact: true,
        path: '/app/management/teams/:teamId',
        component: lazy(() => import('src/views/teams/TeamDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/volunteers/topVolunteers',
        component: lazy(() => import('src/views/volunteer/TopVolunteer'))
      },
      {
        exact: true,
        path: '/app/management/volunteers',
        component: lazy(() => import('src/views/volunteer/VolunteerListView'))
      },
      {
        exact: true,
        path: '/app/management/volunteers/:volunteerId',
        component: lazy(() => import('src/views/volunteer/VolunteerEditView'))
      },
      {
        exact: true,
        path: '/app/management/volunteers/:volunteerId/add_activity',
        component: lazy(() =>
          import('src/views/volunteer/VolunteerAddActivity')
        )
      },
      {
        exact: true,
        path: '/app/management/volunteers/create',
        component: lazy(() => import('src/views/volunteer/VolunteerEditView'))
      },
      {
        exact: true,
        path: '/app/management/beneficiaries/',
        component: lazy(() =>
          import('src/views/beneficiaries/BeneficiaryListView')
        )
      },
      {
        exact: true,
        path: '/app/management/beneficiaries/create',
        component: lazy(() =>
          import('src/views/beneficiaries/BeneficiaryEditView')
        )
      },
      {
        exact: true,
        path: '/app/management/beneficiaries/:beneficiaryId',
        component: lazy(() =>
          import('src/views/beneficiaries/BeneficiaryEditView')
        )
      },
      {
        exact: true,
        path: '/app/management/incidents',
        component: lazy(() => import('src/views/incidents/IncidentListView'))
      },
      {
        exact: true,
        path: '/app/management/incidents/create',
        component: lazy(() => import('src/views/incidents/IncidentCreateView'))
      },
      {
        exact: true,
        path: '/app/management/incidents/:incidentId',
        component: lazy(() => import('src/views/incidents/IncidentDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/orders',
        component: lazy(() => import('src/views/order/OrderListView'))
      },
      {
        exact: true,
        path: '/app/management/orders/:orderId',
        component: lazy(() => import('src/views/order/OrderDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/activities',
        component: lazy(() => import('src/views/activities/ActivityListView'))
      },
      {
        exact: true,
        path: '/app/management/awards',
        component: lazy(() => import('src/views/awards/AwardsListView'))
      },
      {
        exact: true,
        path: '/app/management/awards/:awardId',
        component: lazy(() => import('src/views/awards/AwardsDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/institutions/create',
        component: lazy(() =>
          import('src/views/institutions/InstitutionsDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management/institutions/school-directory-import',
        component: lazy(() =>
          import('src/views/institutions/SchoolDirectoryImportView')
        )
      },
      {
        exact: true,
        path: '/app/management/institutions/:institutionId',
        component: lazy(() =>
          import('src/views/institutions/InstitutionsDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management/institutions',
        component: lazy(() =>
          import('src/views/institutions/InstitutionsListView')
        )
      },
      {
        exact: true,
        path: '/app/management/programmes/:programmesId',
        component: lazy(() =>
          import('src/views/programmes/ProgrammesDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management/programmes/create',
        component: lazy(() =>
          import('src/views/programmes/ProgrammesDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management/programmes',
        component: lazy(() => import('src/views/programmes/ProgrammesListView'))
      },
      {
        exact: true,
        path: '/app/management/activities/create',
        component: lazy(() =>
          import('src/views/activities/ActivityDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management/activities/:activityId',
        component: lazy(() =>
          import('src/views/activities/ActivityDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management',
        component: () => <Redirect to="/app/management/volunteers" />
      },
      {
        exact: true,
        path: '/app/projects/overview',
        component: lazy(() => import('src/views/project/OverviewView'))
      },
      {
        exact: true,
        path: '/app/projects/browse',
        component: lazy(() => import('src/views/project/ProjectBrowseView'))
      },
      {
        exact: true,
        path: '/app/projects/create',
        component: lazy(() => import('src/views/project/ProjectCreateView'))
      },
      {
        exact: true,
        path: '/app/projects/:id',
        component: lazy(() => import('src/views/project/ProjectDetailsView'))
      },
      {
        exact: true,
        path: '/app/projects',
        component: () => <Redirect to="/app/projects/browse" />
      },
      {
        exact: true,
        path: '/app/reports/dashboard',
        component: lazy(() =>
          import('src/views/reports/DashboardAlternativeView')
        )
      },
      {
        exact: true,
        path: '/dashboard',
        component: lazy(() =>
          import('src/views/reports/DashboardAlternativeView')
        )
      },
      {
        exact: true,
        path: '/app/reports',
        component: () => <Redirect to="/app/reports/dashboard" />
      },
      {
        exact: true,
        path: '/app/social/feed',
        component: lazy(() => import('src/views/social/FeedView'))
      },
      {
        exact: true,
        path: '/app/social/profile',
        component: lazy(() => import('src/views/social/ProfileView'))
      },
      {
        exact: true,
        path: '/app/social',
        component: () => <Redirect to="/app/social/profile" />
      },
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/dashboard" />
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '*',
    layout: MainLayout,
    routes: [
      {
        exact: true,
        path: '/',
        component: lazy(() => import('src/views/auth/LoginView'))
      },
      {
        exact: true,
        path: '/support-tickets/:ticketId',
        component: props => <Redirect to={`/app/support-tickets/${props.match.params.ticketId}`} />
      },
      {
        exact: true,
        path: '/support-tickets',
        component: () => <Redirect to="/app/support-tickets" />
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

export default routes;
